import React, { useState, useEffect, useCallback, CSSProperties } from 'react';

interface Slide {
    id: number;
    image: string;
    title: string;
    text: string;
}

const ImageCarousel: React.FC = () => {
    // Dane slajdów
    const slides: Slide[] = [
        {
            id: 1,
            image: '/images/saldo_gate.jpg',
            title: 'Księgowość, która działa dla Ciebie',
            text: 'Nie jesteśmy typowym biurem rachunkowym, które tylko „wbija cyferki w tabelki". Jesteśmy partnerem w biznesie, pomagającym odnaleźć się w gąszczu przepisów i podatków. Od piętnastu lat wspieramy przedsiębiorców – od tych, którzy dopiero startują, po tych, którzy od lat rozwijają swoje firmy. Pomagamy w zakładaniu działalności, rozliczamy różne formy biznesu i zajmujemy się obsługą kadr oraz płac. Jeśli trzeba zoptymalizować podatki, przedstawiamy dostępne możliwości, a w bardziej złożonych przypadkach współpracujemy z ekspertami podatkowymi i prawnikami.'
        },
        {
            id: 2,
            image: '/images/Saldo_main_hall_1.jpg',
            title: 'Zgrany zespół i nowoczesne podejście',
            text: 'W Saldo.pl tworzymy zespół ambitnych i dynamicznych ludzi, którzy lubią to, co robią, i doskonale się na tym znają. Każdy z nas ma bogate doświadczenie i potrafi radzić sobie z nietypowymi sytuacjami. Wiemy, że biznes to nie tylko liczby, ale też relacje, dlatego stawiamy na partnerskie podejście. Łączymy stabilność i rzetelność z nowoczesnymi rozwiązaniami, które ułatwiają życie naszym klientom.'
        },
        {
            id: 3,
            image: '/images/Saldo_main_hall_2.jpg',
            title: 'Księgowość bez granic',
            text: 'Obsługujemy firmy zarówno z Polski, jak i te z kapitałem zagranicznym. Rozliczamy spółki z udziałowcami różnych narodowości, co daje nam unikalne doświadczenie w tym zakresie. Niezależnie od tego, czy prowadzisz działalność w kraju, czy masz wspólników z zagranicy – u nas znajdziesz profesjonalne wsparcie.'
        }
    ];

    const slideDuration = 10000; // 10 sekund
    const [currentSlide, setCurrentSlide] = useState<number>(0);
    const [isPaused, setIsPaused] = useState<boolean>(false);
    const [timeLeft, setTimeLeft] = useState<number>(slideDuration);

    const nextSlide = useCallback(() => {
        setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
        setTimeLeft(slideDuration);
    }, [slides.length]);

    const prevSlide = (): void => {
        setCurrentSlide((prevSlide) => (prevSlide - 1 + slides.length) % slides.length);
        setTimeLeft(slideDuration);
    };

    // Automatyczne przełączanie slajdów
    useEffect(() => {
        if (isPaused) return;

        const intervalId = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 100) {
                    nextSlide();
                    return slideDuration;
                }
                return prev - 100;
            });
        }, 100); // Aktualizacja co 100ms dla płynnej animacji paska postępu

        return () => clearInterval(intervalId);
    }, [isPaused, nextSlide, slideDuration]);

    // Funkcje do ręcznego przełączania slajdów
    const goToSlide = (index: number): void => {
        setCurrentSlide(index);
        setTimeLeft(slideDuration);
    };

    // Style dla komponentu
    const carouselStyle: CSSProperties = {
        margin: 'auto',
        position: 'relative',
        width: '95vw',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    };

    const imageContainerStyle: CSSProperties = {
        position: 'relative',
        width: '100%',
        height: '400px', // Możesz dostosować wysokość zdjęcia
        overflow: 'hidden',
    };

    const slideStyle: CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        transition: 'opacity 0.5s ease-in-out',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    };

    const textContainerStyle: CSSProperties = {
        width: '100%',
        height: 'fit-content',
        minHeight: '130px',
        backgroundColor: '#333', // Ciemne tło dla tekstu
        color: 'white',
        padding: '20px',
        position: 'relative', // Dla paska postępu
    };

    const titleStyle: CSSProperties = {
        color: '#9ACA3C', // Zielony kolor, podobny do tego na stronie
        marginBottom: '10px',
        fontSize: '24px',
    };

    const textStyle: CSSProperties = {
        width: '90%',
        lineHeight: '1.5',
        margin: 0,
    };

    const indicatorsStyle: CSSProperties = {
        position: 'absolute',
        bottom: '20px',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        zIndex: 10,
    };

    const indicatorStyle: CSSProperties = {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        cursor: 'pointer',
    };

    const activeIndicatorStyle: CSSProperties = {
        ...indicatorStyle,
        backgroundColor: 'white',
    };

    const buttonStyle: CSSProperties = {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'transparent',
        color: 'white',
        border: 'none',
        fontSize: '32px',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
        padding: 0,
        margin: 0,
        textShadow: '0 0 5px rgba(0, 0, 0, 0.5)', // Cień dla lepszej widoczności
    };

    const progressBarContainerStyle: CSSProperties = {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '4px',
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
    };

    const progressBarStyle: CSSProperties = {
        height: '100%',
        backgroundColor: '#9ACA3C', // Zielony pasek, podobny do koloru nagłówka
        width: `${(timeLeft / slideDuration) * 100}%`,
        transition: 'width 100ms linear',
    };

    return (
        <div
            style={carouselStyle}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* Kontener na obrazy */}
            <div style={imageContainerStyle}>
                {slides.map((slide, index) => (
                    <div
                        key={slide.id}
                        style={{
                            ...slideStyle,
                            backgroundImage: `url(${slide.image})`,
                            opacity: index === currentSlide ? 1 : 0,
                            zIndex: index === currentSlide ? 1 : 0,
                        }}
                    />
                ))}

                {/* Przyciski nawigacyjne - teraz jako białe strzałki */}
                <button
                    style={{ ...buttonStyle, left: '20px' }}
                    onClick={prevSlide}
                >
                    &#8592; {/* Unicode dla strzałki w lewo */}
                </button>
                <button
                    style={{ ...buttonStyle, right: '20px' }}
                    onClick={nextSlide}
                >
                    &#8594; {/* Unicode dla strzałki w prawo */}
                </button>

                {/* Wskaźniki slajdów */}
                <div style={indicatorsStyle}>
                    {slides.map((_, index) => (
                        <div
                            key={index}
                            style={index === currentSlide ? activeIndicatorStyle : indicatorStyle}
                            onClick={() => goToSlide(index)}
                        />
                    ))}
                </div>
            </div>

            {/* Stały blok tekstowy pod zdjęciem */}
            <div style={textContainerStyle}>
                <h2 style={titleStyle}>{slides[currentSlide].title}</h2>
                <p style={textStyle}>{slides[currentSlide].text}</p>

                {/* Pasek postępu */}
                <div style={progressBarContainerStyle}>
                    <div style={progressBarStyle}></div>
                </div>
            </div>
        </div>
    );
};

export default ImageCarousel;
